import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader.js';

interface ImportError { row: number; name: string; error: string }
interface ImportResult { total: number; created: number; failed: number; errors: ImportError[] }

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? '/api/v1';

export function ImportProducts() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const importMutation = useMutation({
    mutationFn: async (csvText: string) => {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/products/import-csv`, {
        method: 'POST',
        headers: {
          'content-type': 'text/csv; charset=utf-8',
          authorization: token ? `Bearer ${token}` : '',
        },
        body: csvText,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${txt.slice(0, 400)}`);
      }
      return (await res.json()) as ImportResult;
    },
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });

  async function submit() {
    if (!file) return;
    setResult(null);
    // Always read as UTF-8 so Arabic headers/values land correctly.
    const buf = await file.arrayBuffer();
    const csv = new TextDecoder('utf-8').decode(buf);
    importMutation.mutate(csv);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader
        title="Import products from CSV"
        subtitle="Drop a catalog export. Each row becomes a product. Image embeddings happen in the background — within a minute everything is searchable."
      />

      <section className="space-y-4">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center hover:border-brand-500 cursor-pointer"
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
          />
          {file ? (
            <>
              <div className="font-medium text-slate-900">{file.name}</div>
              <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to pick a different file</div>
            </>
          ) : (
            <>
              <div className="font-medium text-slate-900">Drop a CSV here, or click to pick one</div>
              <div className="text-xs text-slate-500 mt-1">Max 1 MB · up to 500 rows per upload</div>
            </>
          )}
        </div>

        <details className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
          <summary className="cursor-pointer font-medium">Expected CSV columns</summary>
          <p className="mt-2 text-slate-600">
            Headers <code>name</code>, <code>category</code>, <code>original_price</code>, <code>discount_price</code>,
            <code> images</code> (JSON array of URLs), <code>mini_image</code>, <code>variants</code> (JSON array
            of <code>{`{color,size,quantity,imageURL,price}`}</code>), <code>is_product_single</code>,
            <code> description</code>, <code>section</code>. Currency is IQD.
          </p>
        </details>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value = ''; }}
            disabled={!file || importMutation.isPending}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            onClick={submit}
            disabled={!file || importMutation.isPending}
            className="rounded-md bg-brand-600 text-white px-4 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {importMutation.isPending ? 'Importing…' : 'Import'}
          </button>
        </div>

        {importMutation.error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
            {(importMutation.error as Error).message}
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
            <div className="grid grid-cols-3 gap-3 text-center mb-3">
              <Stat label="Total rows" value={result.total} />
              <Stat label="Created" value={result.created} accent="green" />
              <Stat label="Failed" value={result.failed} accent={result.failed > 0 ? 'red' : 'slate'} />
            </div>
            {result.errors.length > 0 && (
              <div className="mt-2">
                <div className="font-medium mb-1 text-red-700">Failed rows</div>
                <ul className="text-xs space-y-1 max-h-60 overflow-auto">
                  {result.errors.map((e, i) => (
                    <li key={i} className="font-mono bg-red-50 border border-red-100 px-2 py-1 rounded">
                      row {e.row} — <span className="font-sans">{e.name || '(unnamed)'}</span> — {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                Import another
              </button>
              <button
                onClick={() => navigate('/products')}
                className="rounded-md bg-brand-600 text-white px-3 py-1.5 text-sm hover:bg-brand-700"
              >
                Go to products
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, accent = 'slate' }: { label: string; value: number; accent?: 'slate' | 'green' | 'red' }) {
  const color = accent === 'green' ? 'text-green-700' : accent === 'red' ? 'text-red-700' : 'text-slate-900';
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${color}`}>{value.toLocaleString()}</div>
    </div>
  );
}
